import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import Header from "../../components/Header";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import SendIcon from "@mui/icons-material/Send";
import * as yup from "yup";
import {
  updateAdminAction,
  getAdminProfileActionFn,
} from "../../redux/actions/adminAction";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import LoadingIndicator from "../../components/loadingIndicator";
import { useGetRolesQuery } from "../../state/api";

const validationSchema = yup.object().shape({
  fullname: yup.string().required("Full fullname is required"),
  role: yup.string().required("Role is required"),
  isActive: yup.string().required("Select a status"),
});

const EditAdmin = () => {
  const location = useLocation();
  const { pathname } = location;
  const adminId = pathname.split("/").pop(); //extract adminId from the URL
  const [err, setErr] = useState({});
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isEditable, setIsEditable] = useState(false);

  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    username: "",
    // password: "",
    mobileNumber: "",
    isActive: "",
    role: "",
    roleId: "",
  });

  useEffect(() => {
    const fetchAdminDetails = async () => {
      try {
        const response = await dispatch(getAdminProfileActionFn(adminId));
        if (response && response.success) {
          const adminDetails = response.response;
          setFormData(adminDetails); // Populate form data with admin details
        } else {
          console.log("Failed to fetch admin's details");
          toast.error("Failed to fetch admin's details");
        }
      } catch (error) {
        console.log("Error fetching admin details", error);
        toast.error("Error fetching admin details");
      }
    };

    fetchAdminDetails();
  }, [adminId, dispatch]);

  const { data: roleData = [] } = useGetRolesQuery();

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      setErr({ ...err, [fieldName]: "" });
    } catch (error) {
      setErr({ ...err, [fieldName]: error.message });
    }
  };

  // const handleChange = (e) => {
  //   setFormData((prevState) => ({
  //     ...prevState,
  //     [e.target.name]: e.target.value,
  //   }));
  // };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      let updatedData = {
        ...prevData,
        [name]: value,
      };

      if (name === "roleId") {
        const selectedRole = roleData.find((role) => role._id === value);
        updatedData = {
          ...updatedData,
          role: selectedRole ? selectedRole.title : "",
        };
      }
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true); // Start loading
      await validationSchema.validate(formData, { abortEarly: false });

      const isSuccess = await dispatch(updateAdminAction(adminId, formData));
      if (isSuccess) {
        navigate("/admin");
        toast.success("Admin updated successfully");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error("Something went wrongs");
      }
    } catch (error) {
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErr(validationErrors);
      }
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <Box mt="1.5rem" mb="2.5rem" ml="2.5rem" mr="2.5rem">
      <Header title="EDIT ADMIN" />
      <form onSubmit={handleSubmit}>
        <FormControl fullWidth variant="outlined" margin="normal">
          <TextField
            label="Full Name"
            name="fullname"
            value={formData.fullname}
            onChange={handleChange}
            onBlur={handleBlur}
            fullWidth
            margin="normal"
          />
          {err.fullname && (
            <Grid item>
              <Typography color="error">{err.fullname}</Typography>
            </Grid>
          )}{" "}
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
          />
          {!isEditable && (
            <Typography variant="caption" style={{ color: "green" }}>
              This field is not editable
            </Typography>
          )}
          <TextField
            label="Username"
            name="username"
            value={formData.username}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
          />
          {!isEditable && (
            <Typography variant="caption" style={{ color: "green" }}>
              This field is not editable
            </Typography>
          )}
          <TextField
            label="Mobile Number"
            name="mobileNumber"
            value={formData.mobileNumber}
            fullWidth
            margin="normal"
            InputProps={{ readOnly: true }}
          />
          {!isEditable && (
            <Typography variant="caption" style={{ color: "green" }}>
              This field is not editable
            </Typography>
          )}
          <FormControl fullWidth variant="outlined" margin="normal">
            <div style={{ display: "flex", alignItems: "center" }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Is Active</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value="true">True</MenuItem>
                  <MenuItem value="false">False</MenuItem>
                </Select>{" "}
                {err.isActive && (
                  <Grid item>
                    <Typography color="error">{err.isActive}</Typography>
                  </Grid>
                )}{" "}
              </FormControl>
            </div>
          </FormControl>
          {/* Role */}
          <FormControl
            fullWidth
            variant="outlined"
            margin="normal"
            error={!!err.role}
          >
            <InputLabel htmlFor="role">Roles</InputLabel>
            <Select
              name="roleId"
              value={formData.roleId}
              onChange={handleChange}
              // onBlur={handleBlur}
            >
              {roleData.map((role) => (
                <MenuItem key={role._id} value={role._id}>
                  {role.title}
                </MenuItem>
              ))}
            </Select>
            {err.roleId &&
              formData.roleId.length === 0 && ( // Only display the error if no role is selected
                <Typography variant="caption" color="error">
                  {err.roleId}
                </Typography>
              )}
          </FormControl>
          {/* SUBMIT BUTTON */}
          <Grid container justifyContent="center" alignItems="center">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              disabled={loading}
            >
              Submit
            </Button>
          </Grid>
        </FormControl>
      </form>
      {/* Render loading indicator if loading */}
      {loading && <LoadingIndicator />}
    </Box>
  );
};

export default EditAdmin;
